import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ChevronLeft, Bell, BadgeCheck, ShieldAlert, Video, ThumbsUp, CheckCheck, Users } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { useFollowingActivity } from '../../hooks/useFollowingActivity';
import { supabase } from '../../lib/supabase';

const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};


const headerStyle = {
  backgroundColor: 'var(--header-bg)',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  paddingTop: 'var(--sat)',
};

const skeletonBg = 'var(--skeleton-bg)';

// ── Relative time helper ────────────────────────────────────

const relativeTime = (isoString) => {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(hours / 24);
  if (days >= 7) {
    const d = new Date(isoString);
    const opts = { month: 'short', day: 'numeric' };
    if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
    return d.toLocaleDateString('en-US', opts);
  }
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (mins > 0) return `${mins}m`;
  return 'now';
};

const displayActor = (n) =>
  (n.actor_use_username && n.actor_username) ? n.actor_username : (n.actor_name || 'Someone');

// ── Per-type presentation ───────────────────────────────────

// post_reply, new_follower and mention are absent deliberately — they render the
// actor's avatar instead of a type icon (see showAvatar below).
const TYPE_ICONS = {
  post_milestone: ThumbsUp,
  verification_approved: BadgeCheck,
  verification_rejected: BadgeCheck,
  submission_approved: Video,
  submission_rejected: Video,
  submission_removed: Video,
  post_removed: ShieldAlert,
  reply_removed: ShieldAlert,
};

const notificationCopy = (n) => {
  const quoted = n.context_title ? `"${n.context_title}"` : null;
  switch (n.type) {
    case 'post_reply':
      return { title: `${displayActor(n)} replied to your post`, body: quoted };
    case 'new_follower':
      return { title: `${displayActor(n)} followed you`, body: 'Tap to view their profile' };
    // 053: context_title is the TABLE name here, joined at read time — a
    // mention is someone else's words, so nothing of the post is quoted (026).
    case 'mention':
      return {
        title: `${displayActor(n)} mentioned you`,
        body: !n.link
          ? 'That post is no longer available.'
          : n.context_title ? `In ${n.context_title}` : null,
      };
    case 'post_milestone':
      return n.milestone === 1
        ? { title: 'Someone found your post helpful', body: quoted }
        : { title: `Your post reached ${n.milestone} helpful votes`, body: quoted };
    case 'verification_approved':
      return { title: "You're verified", body: 'Your DSP status was approved.' };
    case 'verification_rejected':
      return { title: 'Your verification needs another look', body: 'Tap to see why and resubmit.' };
    case 'submission_approved':
      return { title: 'Your video was approved', body: quoted };
    case 'submission_rejected':
      // 046: detail is the moderator's reason. Before it, the body was the
      // video's own title, so "wasn't approved" arrived with no way to learn why.
      return { title: "Your video wasn't approved", body: n.detail || quoted };
    // 046: its own type. Reusing submission_rejected here read as "Your video
    // wasn't approved" to a member who had already been told it WAS.
    case 'submission_removed':
      return {
        title: quoted ? `${quoted} was removed from the library` : 'Your video was removed from the library',
        body: n.detail || 'A moderator removed it — see the Community Guidelines.',
      };
    case 'post_removed':
      return { title: 'Your post was removed by a moderator', body: quoted ? `${quoted} — see the Community Guidelines.` : 'See the Community Guidelines.' };
    case 'reply_removed':
      return { title: 'Your reply was removed by a moderator', body: quoted ? `On ${quoted}` : null };
    default:
      return { title: 'Notification', body: quoted };
  }
};

// ── Rows (module scope — in-render definitions cause remount bugs) ──

const ActorAvatar = ({ src, initial }) => (
  src ? (
    <img src={src} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
  ) : (
    <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold bg-gradient-to-br from-brand-primary to-brand-secondary">
      {(initial || 'S').toUpperCase()}
    </div>
  )
);

const NotificationRow = ({ notification: n, isFirst, onOpen }) => {
  const { title, body } = notificationCopy(n);
  const unread = !n.read_at;
  const clickable = !!n.link;
  const TypeIcon = TYPE_ICONS[n.type] || Bell;
  // Both actor-bearing types show a face — that's most of the emotional payload
  const showAvatar = n.type === 'post_reply' || n.type === 'new_follower' || n.type === 'mention';

  return (
    <button
      onClick={() => onOpen(n)}
      className={`w-full flex items-start gap-3 px-4 py-3.5 text-left transition-colors ${clickable ? 'hover-surface' : 'cursor-default'}`}
      style={{
        borderTop: isFirst ? 'none' : '1px solid var(--card-border)',
        backgroundColor: unread ? 'var(--pill-bg)' : 'transparent',
      }}
    >
      {showAvatar ? (
        <ActorAvatar src={n.actor_avatar} initial={displayActor(n)[0]} />
      ) : (
        <div
          className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center"
          style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}
        >
          <TypeIcon size={16} style={{ color: 'var(--brand-primary)' }} />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-snug ${unread ? 'font-semibold' : 'font-medium'} text-primary`}>{title}</p>
        {body && <p className="text-xs text-tertiary mt-0.5 line-clamp-2">{body}</p>}
      </div>

      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <span className="text-xs text-tertiary">{relativeTime(n.created_at)}</span>
        {unread && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--brand-accent)' }} />}
      </div>
    </button>
  );
};

// Following-tab row. No unread dot and no read state — this tab is unbadged
// by design (027): it's a browsable activity list, not a notification queue.
const FollowingRow = ({ item: a, isFirst, onOpen }) => {
  const actor = (a.actor_use_username && a.actor_username) ? a.actor_username : (a.actor_name || 'Someone');
  const context = a.activity_kind === 'video'
    ? `${actor} shared a video`
    : `${actor} posted in ${a.context_label}`;

  return (
    <button
      onClick={() => onOpen(a)}
      className="w-full flex items-start gap-3 px-4 py-3.5 text-left transition-colors hover-surface"
      style={{ borderTop: isFirst ? 'none' : '1px solid var(--card-border)' }}
    >
      <ActorAvatar src={a.actor_avatar} initial={actor[0]} />

      <div className="flex-1 min-w-0">
        <p className="text-sm leading-snug font-semibold text-primary line-clamp-2">{a.item_title}</p>
        <p className="text-xs text-tertiary mt-0.5">{context}</p>
        {a.item_excerpt && (
          <p className="text-xs text-tertiary opacity-70 mt-0.5 line-clamp-1">{a.item_excerpt}</p>
        )}
      </div>

      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        {a.activity_kind === 'video' && a.item_thumbnail && (
          <img src={a.item_thumbnail} alt="" className="w-16 rounded object-cover" style={{ aspectRatio: '16 / 9' }} />
        )}
        <span className="text-xs text-tertiary">{relativeTime(a.activity_at)}</span>
      </div>
    </button>
  );
};

// ── Skeleton ────────────────────────────────────────────────

const RowSkeleton = ({ isFirst }) => (
  <div className="flex items-start gap-3 px-4 py-3.5 animate-pulse" style={{ borderTop: isFirst ? 'none' : '1px solid var(--card-border)' }}>
    <div className="w-9 h-9 rounded-full flex-shrink-0" style={{ backgroundColor: skeletonBg }} />
    <div className="flex-1 space-y-2 pt-1">
      <div className="h-3 rounded w-2/3" style={{ backgroundColor: skeletonBg }} />
      <div className="h-2.5 rounded w-1/2" style={{ backgroundColor: skeletonBg }} />
    </div>
  </div>
);

const ListSkeleton = () => (
  <div className="rounded-2xl overflow-hidden" style={cardStyle}>
    <RowSkeleton isFirst />
    <RowSkeleton />
    <RowSkeleton />
  </div>
);

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className="px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-all active:scale-[0.98]"
    style={active
      ? { backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)', color: 'var(--brand-primary)' }
      : { backgroundColor: 'transparent', border: '1px solid transparent', color: 'var(--text-tertiary)' }}
  >
    {children}
  </button>
);

// ── Main ────────────────────────────────────────────────────

const NotificationsScreen = () => {
  const navigate = useNavigate();
  const { refreshUnread } = useOutletContext() ?? {};
  const { notifications, loading, loadingMore, hasMore, error, fetchMore, markRead, markAllRead } = useNotifications();

  const [tab, setTab] = useState('you');
  // Lazy-mount the Following data: landing on "You" must stay at one RPC.
  const [visitedFollowing, setVisitedFollowing] = useState(false);
  const following = useFollowingActivity(visitedFollowing);

  // Disambiguates the two Following empty states. Owner-read RLS scopes the
  // count to the caller's own follow rows.
  const [followCount, setFollowCount] = useState(null);
  useEffect(() => {
    if (!visitedFollowing) return undefined;
    let cancelled = false;
    (async () => {
      const { count } = await supabase
        .from('user_follows')
        .select('followee_id', { count: 'exact', head: true });
      if (!cancelled) setFollowCount(count ?? 0);
    })();
    return () => { cancelled = true; };
  }, [visitedFollowing]);

  const unreadCount = notifications.filter(n => !n.read_at).length;

  const handleOpen = (n) => {
    if (!n.read_at) {
      // Refresh the badge only after the RPC lands — a concurrent recount
      // races the UPDATE and can read the pre-update count
      markRead(n.id).then(() => refreshUnread?.(true));
    }
    if (n.link) navigate(n.link);
  };

  const handleOpenActivity = (a) => {
    if (a.link) navigate(a.link);
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
    refreshUnread?.(true);
  };

  const showTab = (next) => {
    setTab(next);
    if (next === 'following') setVisitedFollowing(true);
  };

  // "Mark all read" belongs to the You tab only
  const showMarkAll = tab === 'you' && unreadCount > 0;

  return (
    <div className="min-h-screen pb-nav">
      {/* Mobile sticky header */}
      <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
        <div className="max-w-2xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)} className="p-1 -ml-1 transition-all hover:scale-110 active:scale-95">
              <ChevronLeft size={22} className="text-white" />
            </button>
            <h1 className="text-lg font-bold text-white">Notifications</h1>
          </div>
          {showMarkAll && (
            <button
              onClick={handleMarkAllRead}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:bg-white/25 active:scale-[0.98]"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)' }}
            >
              <CheckCheck size={14} />
              Mark all read
            </button>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Desktop header replacement */}
        <div className="hidden md:flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-primary">Notifications</h1>
          {showMarkAll && (
            <button
              onClick={handleMarkAllRead}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-secondary transition-all hover:brightness-110 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--card-border)' }}
            >
              <CheckCheck size={14} />
              Mark all read
            </button>
          )}
        </div>

        {/* Tabs — in the body, not the sticky header, so the header keeps its
            height and "Mark all read" stays where users expect it. */}
        <div className="flex items-center gap-1 mb-3">
          <TabButton active={tab === 'you'} onClick={() => showTab('you')}>You</TabButton>
          <TabButton active={tab === 'following'} onClick={() => showTab('following')}>Following</TabButton>
        </div>

        {tab === 'you' ? (
          loading ? (
            <ListSkeleton />
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Couldn't load your notifications. Pull to refresh or try again later.
              </p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-center">
              <Bell size={32} style={{ color: 'var(--text-tertiary)' }} strokeWidth={1.5} />
              <p className="text-sm font-medium text-tertiary">No notifications yet</p>
              <p className="text-xs text-tertiary opacity-60">Replies, helpful votes, and updates will show up here.</p>
            </div>
          ) : (
            <>
              <div className="rounded-2xl overflow-hidden" style={cardStyle}>
                {notifications.map((n, i) => (
                  <NotificationRow key={n.id} notification={n} isFirst={i === 0} onOpen={handleOpen} />
                ))}
              </div>

              {hasMore && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={fetchMore}
                    disabled={loadingMore}
                    className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
                    style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--brand-primary)', border: '1px solid var(--pill-border)' }}
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )
        ) : (
          following.loading ? (
            <ListSkeleton />
          ) : following.error ? (
            <div className="text-center py-12">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Couldn't load activity. Pull to refresh or try again later.
              </p>
            </div>
          ) : following.items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-center">
              <Users size={32} style={{ color: 'var(--text-tertiary)' }} strokeWidth={1.5} />
              {followCount === 0 ? (
                <>
                  <p className="text-sm font-medium text-tertiary">You're not following anyone yet</p>
                  <p className="text-xs text-tertiary opacity-60">Follow DSPs from their profile to see their posts here.</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-tertiary">Nothing new</p>
                  <p className="text-xs text-tertiary opacity-60">Posts and videos from the people you follow will show up here.</p>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="rounded-2xl overflow-hidden" style={cardStyle}>
                {following.items.map((a, i) => (
                  <FollowingRow key={`${a.activity_kind}-${a.item_id}`} item={a} isFirst={i === 0} onOpen={handleOpenActivity} />
                ))}
              </div>

              {following.hasMore && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={following.fetchMore}
                    disabled={following.loadingMore}
                    className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
                    style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--brand-primary)', border: '1px solid var(--pill-border)' }}
                  >
                    {following.loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
};

export default NotificationsScreen;
